import { valibotResolver } from "@hookform/resolvers/valibot";
import { LinkedinLogo } from "@phosphor-icons/react";
import { type Dispatch, type SetStateAction, useState } from "react";
import { useForm } from "react-hook-form";

import ExtraSettings from "common/components/CreateCampaignModal/CampaignSettings/ExtraSettings";
import MaxLengthInput from "common/components/MaxLengthInput";
import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerDescription, DrawerTitle } from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import WorkspaceAccountSelector from "common/components/WorkspaceAccountSelector";
import { CAMPAIGN_TYPES } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useCampaigns from "common/datahooks/useCampaigns";
import { getSchemaMaxLength } from "common/helpers/utils";
import useAppStore from "common/hooks/useAppStore";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWorkspaceNavigate from "common/hooks/useWorkspaceNavigate";
import {
  CampaignNameSchema,
  type CampaignSettingsSchemaType,
  DefaultCampaignSettingsSchema,
  DripCampaignSettingsSchema,
  type ExtraCampaignSettingsSchemaType,
  NavigatorCampaignSettingsSchema,
  SearchCampaignSettingsSchema,
} from "common/schemas";
import type { CampaignOutreachType, CampaignType } from "common/types";

import useCampaignMutations from "../../../../pages/campaigns/datahooks/useCampaignMutations";
import AudienceSize from "./AudienceSize";
import ItemContainer from "./ItemContainer";
import OutreachType from "./OutreachType";

interface CampaignSettingsProps {
  type: CampaignType;
  outreachType: CampaignOutreachType;
  setOutreachType: Dispatch<SetStateAction<CampaignOutreachType>>;
  goBack: () => void;
  resetState: () => void;
}

export default function CampaignSettings({
  type,
  outreachType,
  setOutreachType,
  goBack,
  resetState,
}: CampaignSettingsProps) {
  const isDesktop = useTwBreakpoint("lg");

  const { label } = CAMPAIGN_TYPES[type];

  const { relativeNavigate } = useWorkspaceNavigate();
  const { accounts } = useSelectedWorkspaceContext();

  const navigatorAccount = accounts.filter(
    ({ sales_navigator, state }) => sales_navigator && state === "LoggedIn",
  )[0];

  const isList = type === "list" || type === "crm";

  const { campaigns } = useCampaigns();
  const { createCampaign, isCreatingCampaign } = useCampaignMutations();

  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([
    accounts[0].id,
  ]);

  const setIsCampaignModalOpen = useAppStore(
    (state) => state.setIsCampaignModalOpen,
  );

  const workerAccounts = selectedAccountIds.length
    ? selectedAccountIds
    : accounts.map(({ id }) => id);

  function onSubmit({
    name,
    size,
    extraSettings,
  }: { name: string; size: number } & {
    extraSettings: ExtraCampaignSettingsSchemaType;
  }) {
    createCampaign({
      name,
      type,

      // List campaigns don't require miner account
      ...(isList
        ? []
        : {
            miner:
              type === "navigator" ? navigatorAccount.id : workerAccounts[0],
          }),

      // Drip campaigns don't have limit for audience size and don't have a option for inmail optimization
      ...(type === "drip"
        ? { inmail_optimization: false }
        : { audience_size: size }),

      outreach_type: outreachType,
      account_ids: workerAccounts,

      ...extraSettings,
    }).then((campaign) => {
      setIsCampaignModalOpen(false);
      resetState();
      relativeNavigate(`campaigns/${campaign.id}`);
    });
  }

  let validationSchema: CampaignSettingsSchemaType =
    DefaultCampaignSettingsSchema;
  if (type === "search") {
    validationSchema = SearchCampaignSettingsSchema;
  } else if (type === "drip") {
    validationSchema = DripCampaignSettingsSchema;
  } else if (type === "navigator") {
    validationSchema = NavigatorCampaignSettingsSchema;
  }

  const isDrip = outreachType === "drip";

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      name: `${label} Campaign${campaigns ? ` #${campaigns.length + 1}` : ""}`,
      size: isList ? 10000 : 250,
      extraSettings: {
        exclude_active_targets: true,
        exclude_previous_targets: !isDrip,
        no_pfp: true,
      },
    },
    resolver: valibotResolver(validationSchema),
  });

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <>
      <Title>Create a new campaign</Title>

      <Description className="mb-8">
        {isDrip ? "Inbound" : "Outbound"} {label}
      </Description>

      <FormField
        label="Campaign name"
        htmlFor="campaignName"
        className="mb-5"
        error={errors.name?.message}
      >
        <MaxLengthInput
          variant="md"
          id="campaignName"
          placeholder="Campaign name"
          type="text"
          characterLength={watch("name").length}
          maxLength={getSchemaMaxLength(CampaignNameSchema.entries.name)}
          {...register("name")}
        />
      </FormField>

      <div className="flex flex-col overflow-y-auto scrollbar-thin divide-y divide-black-200 lg:mb-4">
        <ItemContainer
          icon={LinkedinLogo}
          title={`LinkedIn Account${isDrip ? "" : "s"}`}
          description="What LinkedIn account will run this campaign"
          isDrip={isDrip}
        >
          <WorkspaceAccountSelector
            selection={isDrip ? selectedAccountIds[0] : selectedAccountIds}
            setSelection={(selection) => {
              if (Array.isArray(selection)) {
                setSelectedAccountIds(selection);
              } else {
                setSelectedAccountIds([selection]);
              }
            }}
            className="w-48 max-lg:ml-12.5"
          />
        </ItemContainer>

        <OutreachType
          outreachType={outreachType}
          setOutreachType={setOutreachType}
          campaignType={type}
        />

        <AudienceSize
          campaignType={type}
          errorMessage={errors.size?.message}
          register={register}
          isDrip={isDrip}
        />

        <ExtraSettings
          isDrip={isDrip}
          settings={watch("extraSettings")}
          setSettings={(newSettings) => setValue("extraSettings", newSettings)}
        />
      </div>

      <div className="flex justify-between mt-4">
        <Button
          onClick={goBack}
          variant="secondary-black"
          disabled={isCreatingCampaign}
        >
          Back
        </Button>

        <Button onClick={handleSubmit(onSubmit)} isLoading={isCreatingCampaign}>
          Create{isDesktop ? " campaign" : ""}
        </Button>
      </div>
    </>
  );
}
